/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: Collaboration
 * (C) Copyright IBM Corp. 2017, 2022
 *
 * US Government Users Restricted Rights - Use, duplication or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */

define('collaboration/messaging/connectors/msteams/MSTeamsConnector',['../ConnectorBase', './MSTeamsClient', '../../../nls/StringResources'], function (ConnectorBase, MSTeamsClient, StringMessages) {
	'use strict';

	var MSTeamsConnector = ConnectorBase.extend( /** @lends MSTeamsConnector */{

		/**
   * @desc Constructor for MSTeamsConnector.
   * @constructs MSTeamsConnector
   * @extends ConnectorBase
   * @public
   * @param options {Object} Options
   * @param options.glassContext {Object} The glass context
   */
		init: function init(options) {
			MSTeamsConnector.inherited('init', this, arguments);
			this.client = new MSTeamsClient(options.glassContext);
		},

		/**
   * Configure this connector with some meta info.
   * @instance
   * @param {object} meta meta data specific to this connector type.
   */
		configure: function configure(meta) {
			MSTeamsConnector.inherited('configure', this, arguments);

			var isValid = meta.features && meta.features.me && meta.features.me.endpoint && meta.features.teams && meta.features.teams.endpoint && meta.features.channels && meta.features.channels.endpoint && meta.features.messaging && meta.features.messaging.endpoint;

			if (!isValid) {
				throw new Error(StringMessages.get('error_invalid_configuration'));
			}
		},

		/**
   * Authenticate to MS Teams.
   * @instance
   * @param {object} options options are not used at the moment.
   * @returns {promise}
   */
		authenticate: function authenticate(options) {
			void options;
			return this.client.signIn().catch(this.errorHandler);
		},

		/**
   * Send a message to MS Teams.
   * @instance
   * @param {object} options Options
   * @param {string} options.teamId Team ID
   * @param {string} options.channelId {string} Channel ID
   * @param {string} options.message {string} Message text
   * @param {string} options.url {string} URL to CA asset
   * @returns {promise}
   */
		send: function send(data) {
			// some validations...
			if (!data.teamId) {
				return Promise.reject(new Error(StringMessages.get('error_missing_team_id'))).catch(this.errorHandler);
			} else if (!data.recipientId) {
				return Promise.reject(new Error(StringMessages.get('error_missing_channel_id'))).catch(this.errorHandler);
			} else if (!data.message && !data.url) {
				return Promise.reject(new Error(StringMessages.get('error_missing_message'))).catch(this.errorHandler);
			}
			return this.client.sendMessage({
				url: this.meta.features.messaging.endpoint,
				data: {
					team: data.teamId,
					dataUrl: data['dataUrl'],
					recipientType: data.recipientType,
					recipient: data.recipientId,
					recipientName: data.recipientName,
					message: data.message,
					attachments: data.url ? [{
						title: StringMessages.get(data.assetTitle && 'ca_link' || 'ca_link_short', data),
						subTitle: StringMessages.get('product'),
						title_link: data.url
					}] : []
				}
			}).catch(this.errorHandler);
		},

		/**
   * Generate HTML content of the message to send.
   * @instance
   * @private
   * @param {object} data
   */
		_generateMessageContent: function _generateMessageContent(data) {
			var content = '<p>';
			// Split the message into lines, including empty lines.
			var lines = data.message.split(/\r?\n/);
			// Insert <br> tag after each line to preserve message formatting.
			for (var i = 0; i < lines.length; i++) {
				content = content + lines[i] + '<br>';
			}
			content = content + '</p>';
			if (data.url) {
				var caLink = '<p> <a href=\'' + data.url + '\'>' + StringMessages.get('ca_link') + '</a> </p>';
				content = content + caLink;
			}
			return content;
		},

		/**
   * Retrieve current user's joined teams from MS Teams.
   * @instance
   * @returns {promise}
   */
		getJoinedTeams: function getJoinedTeams() {
			return this.loadFeatures().then(this._getJoinedTeams.bind(this));
		},

		_getJoinedTeams: function _getJoinedTeams() {
			return this.client.doGet({
				url: this.meta.features.teams.endpoint
			}).then(function (result) {
				return result && result.data && result.data.value || [];
			}).catch(this.errorHandler);
		},

		/**
   * Retrieve channels for the given team.
   * @instance
   * @param team {Object} Team object.
   * @returns {promise}
   */
		getTeamChannels: function getTeamChannels(team) {
			var url = this.meta.features.channels.endpoint.replace('{teamId}', team.id);
			return this.client.doGet({
				url: url
			}).then(function (result) {
				return result && result.data && result.data.value || [];
			}).catch(this.errorHandler);
		},

		/**
   * Calls feature 'me' to check if the provider is ready for use.
   * @override
   */
		check: function check() {
			return this.loadFeatures().then(function () {
				return this.client.doGet({
					url: this.meta.features.me.endpoint
				}).then(function (response) {
					this.me = response.data || {};
				}.bind(this)).catch(this.errorHandler);
			}.bind(this));
		},

		/**
  * Retrieve all users.
  * @instance
  * @returns {promise}
  */
		getUsers: function getUsers() {
			return this.client.doGet({
				url: this.meta.features.users.endpoint
			}).then(function (result) {
				return result.data.value;
			}).catch(this.errorHandler);
		},

		/**
  * Retrieve chats.
  * @instance
  * @returns {promise}
  */
		getChats: function getChats() {
			return this.loadFeatures().then(this._getChats.bind(this));
		},

		_getChats: function _getChats() {
			return this.client.doGet({
				url: this.meta.features.chats.endpoint
			}).then(function (result) {
				return result.data.value;
			}).catch(this.errorHandler);
		},

		/**
  * Retrieve chat members.
  * @instance
  * @returns {promise}
  */
		getMembersFromChat: function getMembersFromChat(chat) {
			var chatUrl = this.meta.features.members.endpoint;
			chatUrl = chatUrl.replace('{chatId}', chat.id);
			return this.client.doGet({
				url: chatUrl
			}).then(function (result) {
				return result.data.value;
			}).catch(this.errorHandler);
		},

		/**
  * Retrieve user's photo.
  * @instance
  * @returns {promise}
  */
		getPhotoFromUser: function getPhotoFromUser(user) {
			var photoUrl = this.meta.features.photos.endpoint;
			photoUrl = photoUrl.replace('{userId}', user.id);
			return this.client.doGet({
				url: photoUrl
			}).then(function (result) {
				return result.data;
			}).catch(this.errorHandler);
		},

		/**
  * Retrieve team members based on teamId.
  * @instance
  * @returns {promise}
  */
		getMembersFromTeam: function getMembersFromTeam(team) {
			var teamMembersUrl = this.meta.features.teamMembers.endpoint;
			teamMembersUrl = teamMembersUrl.replace('{teamId}', team.id);
			return this.client.doGet({
				url: teamMembersUrl
			}).then(function (result) {
				return result.data.value;
			}).catch(this.errorHandler);
		},

		/**
  * Retrieve chat based on recipient ID.
  * @instance
  * @returns {promise}
  */
		getChatFromRecipientId: function getChatFromRecipientId(recipientId) {
			var chatUrl = this.meta.features.chat.endpoint;
			chatUrl = chatUrl.replace('{recipientId}', recipientId);
			return this.client.doGet({
				url: chatUrl
			}).then(function (result) {
				return result.data;
			}).catch(this.errorHandler);
		},

		/**
  * Create a chat based on a list of recipient IDs.
  * @instance
  * @returns {promise}
  */
		createChatFromRecipientIds: function createChatFromRecipientIds(recipientList) {
			var url = this.meta.features['multi-chat'].endpoint;
			return this.client.doPost({
				url: url,
				data: { 'recipients': recipientList }
			}).then(function (result) {
				return result.data;
			}).catch(this.errorHandler);
		},

		/**
  * Retrieve all recipients (users, groupchats, channels) based on the team.
  * @instance
  * @returns {promise}
  */
		getRecipientsFromTeamId: function getRecipientsFromTeamId(team) {
			var recipientsUrl = this.meta.features.recipients.endpoint;
			recipientsUrl = recipientsUrl.replace('{teamId}', team.id);
			return this.client.doGet({
				url: recipientsUrl
			}).then(function (result) {
				return result.data;
			}).catch(this.errorHandler);
		}
	});

	return MSTeamsConnector;
});
;

var _temp = function () {
	if (typeof __REACT_HOT_LOADER__ === 'undefined') {
		return;
	}
}();

;

